import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const ROCC = "https://customer-assets-4nw71qhi.emergentagent.net/job_venetian-villas/artifacts";
const IMG_CIAN = `${ROCC}/zf2ekvra_pian%20villa.webp`;
const IMG_RAPHAEL = `${ROCC}/4m5haiin_andrea%20navagero%20e%20Agostino%20Beazzano.webp`;
const IMG_GRANARY = `${ROCC}/1oefag6c_ex-granaio%20pasin.webp`;
const IMG_PORTICHETTO = `${ROCC}/gvu4by8s_colonne%20longhena.webp`;

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const Caption = ({ children }) => (
  <p className="mt-3 text-xs uppercase tracking-[0.18em] text-[#1A1A1A]/50 leading-relaxed">{children}</p>
);

export const History = ({ t }) => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="storia" data-testid="history-section" className="py-24 lg:py-32 bg-[#EBE7DF]/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.35em] text-[#C29B62]">{t.history.overline}</p>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-[#1C3829]">{t.history.title}</h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={imgRef} className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-10">
            <motion.figure variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
              <div className="overflow-hidden rounded-t-[10rem] border border-[#C29B62]/20 h-[26rem]">
                <motion.img
                  src={IMG_CIAN}
                  alt={t.history.captions.cian}
                  style={{ y }}
                  className="w-full h-[115%] object-cover"
                  loading="lazy"
                  data-testid="history-image-cian"
                />
              </div>
              <Caption>{t.history.captions.cian}</Caption>
            </motion.figure>
            <motion.figure variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
              <div className="overflow-hidden border border-[#C29B62]/20">
                <img
                  src={IMG_RAPHAEL}
                  alt={t.history.captions.raphael}
                  className="w-full h-56 object-cover"
                  loading="lazy"
                  data-testid="history-image-raphael"
                />
              </div>
              <Caption>{t.history.captions.raphael}</Caption>
            </motion.figure>
          </div>

          <div className="lg:col-span-8 space-y-16">
            {t.history.chapters.map((ch, idx) => (
              <div key={ch.num}>
                <motion.div
                  data-testid={`history-chapter-${ch.num}`}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                  className="grid grid-cols-[auto_1fr] gap-6 lg:gap-10 border-t border-[#C29B62]/25 pt-10"
                >
                  <span className="font-serif-display text-5xl lg:text-7xl font-light text-[#C29B62]/50 leading-none">{ch.num}</span>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-light tracking-tight text-[#1C3829]">{ch.title}</h3>
                    {(ch.paras || [ch.text]).filter(Boolean).map((p, i) => (
                      <p key={i} className="mt-4 text-base text-[#4A4A4A] font-light leading-relaxed">{p}</p>
                    ))}
                  </div>
                </motion.div>
                {idx === 1 && (
                  <motion.div
                    variants={reveal}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                    className="mt-10 grid grid-cols-2 gap-6"
                    data-testid="history-archive-images"
                  >
                    <figure>
                      <div className="overflow-hidden border border-[#C29B62]/20">
                        <img src={IMG_GRANARY} alt={t.history.captions.granary} loading="lazy" className="w-full h-48 lg:h-64 object-cover hover:scale-105 transition-transform duration-700" />
                      </div>
                      <Caption>{t.history.captions.granary}</Caption>
                    </figure>
                    <figure>
                      <div className="overflow-hidden border border-[#C29B62]/20">
                        <img src={IMG_PORTICHETTO} alt={t.history.captions.portichetto} loading="lazy" className="w-full h-48 lg:h-64 object-cover hover:scale-105 transition-transform duration-700" />
                      </div>
                      <Caption>{t.history.captions.portichetto}</Caption>
                    </figure>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
